import { ChevronDown, Minus, Plus, Star, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useCart } from "../../../context/CartContext";

const ServiceDetailsOverlay = ({ service, open, onClose }) => {
  const {
    name,
    imageUrl,
    ratings,
    duration,
    type = [],
    extraCharges = [],
    description = [],
  } = service || {};

  const { cart, addService, removeService } = useCart();
  const [closing, setClosing] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);
  const isVariantBased = Array.isArray(type) && type.length > 0;

  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    if (!open || !isVariantBased) return;

    const cartItem = cart.find((i) => i.serviceId === service._id);

    if (cartItem?.brand) {
      const matched = type.find((v) => v.brand === cartItem.brand) ?? null;
      setSelectedVariant(matched);
    } else {
      setSelectedVariant(null);
    }
  }, [open, cart, service._id, isVariantBased, type]);

  const existing = cart.find((i) => i.serviceId === service._id);

  const qty = existing?.qty ?? 0;
  const isMobile = window.innerWidth < 1024; // lg breakpoint

  const isSinglePrice =
    !isVariantBased &&
    typeof service?.originalPrice === "number" &&
    typeof service?.discountedPrice === "number" &&
    service.discountedPrice < service.originalPrice;

  const topDiscountText = isSinglePrice
    ? service.discount?.type === "percentage"
      ? `${Math.round(
          ((service.originalPrice - service.discountedPrice) /
            service.originalPrice) *
            100,
        )}% OFF`
      : `₹${service.originalPrice - service.discountedPrice} OFF`
    : null;

  const validExtraCharges = Array.isArray(extraCharges)
    ? extraCharges.filter((c) => Number(c?.amount) > 1)
    : [];

  const handleIncrement = () => {
    let variantToUse = selectedVariant;

    if (isVariantBased && !variantToUse) {
      variantToUse = type[0]; // auto-pick ONLY on Add
      setSelectedVariant(variantToUse);
    }

    addService({
      serviceId: service._id,
      title: service.name,
      imageUrl: service.imageUrl,
      brand: variantToUse?.brand ?? null,
      originalPrice: variantToUse
        ? variantToUse.originalPrice
        : service.originalPrice,
      discountedPrice: variantToUse
        ? variantToUse.discountedPrice
        : service.discountedPrice,
      qty: qty + 1,
    });
  };

  const handleDecrement = () => {
    if (qty <= 1) {
      removeService(service._id);
      return;
    }

    addService({
      serviceId: service._id,
      title: service.name,
      imageUrl: service.imageUrl,
      brand: selectedVariant?.brand ?? null,
      originalPrice: existing.originalPrice,
      discountedPrice: existing.discountedPrice,
      qty: qty - 1,
    });
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);

    //  If item already in cart, SWAP the variant
    if (qty > 0) {
      addService({
        serviceId: service._id,
        title: service.name,
        imageUrl: service.imageUrl,
        brand: variant.brand,
        originalPrice: variant.originalPrice,
        discountedPrice: variant.discountedPrice,
        qty,
      });
    }
  };

  const handleClose = () => {
    if (!isMobile) {
      onClose();
      return;
    }

    setClosing(true);

    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 300);
  };

  useEffect(() => {
    if (open) setOpenIndex(0);
    if (!open) return;

    // Lock background scroll
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      // Restore scroll
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, [open]);

  if (!open && !closing) return null;

  return (
    <div className="drawer-container ">
      {/* BACKDROP */}
      <div onClick={handleClose} className="fixed inset-0 bg-black/50 z-40" />

      <div
        className={`fixed z-50  bg-brandSurface ${
          isMobile
            ? `
          bottom-0 left-0 right-0
          rounded-t-2xl
          max-h-[80vh]
          ${closing ? "animate-slideDown" : "animate-slideUp"}
        `
            : `
          top-[56%] left-1/2
          -translate-x-1/2 -translate-y-1/2
          rounded-2xl
          w-full max-w-2xl overflow-hidden shadow-xl
        `
        } max-h-[80vh] overflow-y-auto no-scrollbar
  `}
      >
        {/* HEADER */}
        <div className="sticky top-0 bg-white z-10 p-4 border-b">
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold line-clamp-1 text-primaryTextColor">
                {name}
              </h2>

              {/* META ROW */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 mt-1">
                {/* Rating */}
                {ratings > 0 && (
                  <span className="flex items-center gap-1">
                    {ratings}
                    <Star
                      size={14}
                      className="fill-yellow-500 text-yellow-500"
                    />
                  </span>
                )}

                {/* Duration */}
                {duration && <span>{duration} ⏱ </span>}

                {/* Discount */}
                {topDiscountText && (
                  <span className="px-2 py-[2px] rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    {topDiscountText}
                  </span>
                )}
              </div>
            </div>

            {/* Close */}
            <button onClick={handleClose} className="shrink-0">
              <X />
            </button>
          </div>
        </div>

        <div className="px-4 lg:px-0 rounded-sm overflow-hidden">
          {/* IMAGE */}
          <img src={imageUrl} alt={name} className="w-full h-48 object-cover" />
        </div>

        {/* PRICING */}
        <div className="p-4 space-y-3">
          {type.length > 0 ? (
            <div className="space-y-2">
              {type.map((v) => {
                const hasDiscount = v.discountedPrice < v.originalPrice;
                const isSelected = selectedVariant?.brand === v.brand;

                return (
                  <button
                    key={v.brand}
                    onClick={() => handleVariantSelect(v)}
                    className={` w-full flex justify-between items-center border rounded-lg px-3 py-2 transition ${isSelected ? "border-accentTextColor bg-accentTextColor/5" : "border-gray-300"} `}
                  >
                    {/* BRAND */}
                    <span className="font-medium">{v.brand}</span>

                    {/* PRICE */}
                    <span className="flex items-center gap-2 text-sm">
                      {hasDiscount && (
                        <span className="flex items-center gap-2 text-xs text-green-600 font-semibold">
                          <span className="text-[0.66rem]">
                            {service.discount?.type === "percentage"
                              ? `${Math.round(
                                  ((v.originalPrice - v.discountedPrice) /
                                    v.originalPrice) *
                                    100,
                                )}% OFF`
                              : `₹${v.originalPrice - v.discountedPrice} OFF`}
                          </span>
                          <span className="line-through text-gray-400 text-sm">
                            ₹{v.originalPrice}
                          </span>
                        </span>
                      )}

                      <span className="font-semibold text-accentTextColor">
                        ₹{v.discountedPrice}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              {service.discountedPrice < service.originalPrice && (
                <span className="line-through text-gray-400">
                  ₹{service.originalPrice}
                </span>
              )}

              <span className="font-semibold text-accentTextColor">
                ₹{service.discountedPrice}
              </span>
            </div>
          )}
        </div>

        {/* EXTRA CHARGES */}
        {validExtraCharges.length > 0 && (
          <div className="px-4">
            <h3 className="font-semibold text-primaryTextColor mb-2">
              Extra Charges (if applicable)
            </h3>

            <div className="space-y-2">
              {validExtraCharges.map((c) => (
                <div
                  key={c._id}
                  className="
            flex items-center justify-between
            bg-white
            border
            rounded-lg
            px-3 py-2
          "
                >
                  {/* LEFT */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">{c.title}</span>
                  </div>

                  {/* RIGHT */}
                  <span className="font-semibold text-gray-600">
                    ₹{Number(c.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DESCRIPTION (FAQ STYLE) */}
        <div className="p-4 space-y-2">
          {description.map((d, idx) => {
            const isOpen = idx === openIndex;

            return (
              <div
                key={d._id}
                className="border bg-white rounded-lg overflow-hidden transition"
              >
                {/* HEADER */}
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                  className=" w-full px-4 py-3 flex justify-between items-center font-medium text-left "
                >
                  <span>{d.heading}</span>

                  <ChevronDown
                    size={18}
                    className={` transition-transform duration-300 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    } `}
                  />
                </button>

                {/* BODY (ANIMATED) */}
                <div
                  className={`
            grid transition-all duration-300 ease-in-out
            ${
              isOpen
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0"
            }
          `}
                >
                  <div className="overflow-hidden">
                    <div className="px-4 pb-3 text-sm text-gray-600 space-y-1">
                      {d.points.map((p, i) => (
                        <p key={i}>• {p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* BOTTOM CTA */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex items-center justify-between gap-3">
          {/* LEFT ACTION (ADD or QTY CONTROLLER) */}
          {qty === 0 ? (
            <button
              onClick={handleIncrement}
              className=" w-32 h-12 border border-accentTextColor text-accentTextColor rounded-lg font-semibold text-sm flex items-center justify-center transition "
            >
              Add
            </button>
          ) : (
            <div className=" flex items-center justify-between border border-accentTextColor rounded-lg w-32 h-12 ">
              <button
                onClick={handleDecrement}
                className=" flex-1 h-full flex items-center justify-center text-accentTextColor "
              >
                <Minus size={18} />
              </button>

              <span className="flex-1 text-center font-semibold text-primaryTextColor">
                {qty}
              </span>

              <button
                onClick={handleIncrement}
                className="flex-1 h-full flex items-center justify-center text-accentTextColor"
              >
                <Plus size={18} />
              </button>
            </div>
          )}

          {/* RIGHT ACTION */}
          <div className="flex flex-1 justify-end items-center">
            <button
              onClick={handleClose}
              className=" h-12 w-32 text-sm border border-accentTextColor text-accentTextColor rounded-lg font-semibold transition "
            >
              {qty > 0 ? "Done" : "Browse More"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsOverlay;
